// Single source of truth for checklist status values, human-readable labels,
// board bucketing, the action vocabulary, the note-requirement rule, and the
// Recall ownership predicate. Mirrors the backend's `statusNoteRules.ts` (see
// docs/TRD/checklist-status-change-notes.md §3.3, §3.8).
//
// Pure module: no imports, no JSX. Every export is a total function — it
// returns a defined value for any input, including `undefined`/`null`
// checklists, because the board can render them during loading states.

const STATUS = Object.freeze({
  NEW: 0,
  IN_PROGRESS: 1,
  SUBMITTED: 2,
  IN_REVIEW: 3,
  APPROVED: 4,
});

const STATUS_LABEL = Object.freeze({
  0: "New",
  1: "Open",
  2: "Submitted",
  3: "In Review",
  4: "Approved",
});

const ACTIONS = Object.freeze({
  COMPLETE: "complete",
  APPROVE: "approve",
  RETURN: "return",
  RECALL: "recall",
});

const ACTION_LABEL = Object.freeze({
  complete: "completed",
  approve: "approved",
  return: "returned",
  recall: "recalled",
});

// A note is mandatory on exactly one action. Recall and Return-from-In-Review
// share the same {2,3} -> 1 status pair, so the rule must be keyed on the
// action identity (which side of that pair a given transition is), never on
// the status pair itself (TRD D9, AC-F2.9).
const isNoteRequiredForAction = (action) => action === ACTIONS.RETURN;

/**
 * F12 ownership predicate: is `userId` the person who submitted this
 * checklist, and therefore entitled to Recall rather than Return?
 *
 * `localStorage.getItem("user_id")` is a string in this codebase (existing
 * test fixtures set it via `localStorage.setItem("user_id", "42")`), while
 * `checklist.submitted_by` comes back from the API as a number. A strict
 * `===` comparison would silently and permanently hide Recall from every
 * submitter (TR5), so both sides are coerced with `Number(...)` before
 * comparing.
 *
 * Fails closed: a null/undefined/non-numeric `submitted_by` or `userId`
 * returns `false` rather than granting Recall (AC-F12.5).
 *
 * Must never reference `foreman_user_id` or `general_foreman_user_id` —
 * those columns are deprecated and out of scope for this comparison (NG19).
 */
const canRecall = (checklist, userId) => {
  if (!checklist) return false;

  const submittedBy = Number(checklist.submitted_by);
  const actor = Number(userId);

  if (checklist.submitted_by === null || checklist.submitted_by === undefined) return false;
  if (userId === null || userId === undefined) return false;
  if (Number.isNaN(submittedBy) || Number.isNaN(actor)) return false;

  return submittedBy === actor;
};

const NOTE_MAX_LENGTH = 2000;
const NOTE_MIN_LENGTH = 5;

// Board column bucketing (MyProjectCheckList.js:158-168). Preserved exactly
// as the board implements it today (NG15) — including status `5` in the
// Approved bucket, a frontend-only artefact this module documents but does
// not "clean up" or add to STATUS (AC-F7.5).
const isOpenBucket = (status) => status === STATUS.NEW || status === STATUS.IN_PROGRESS;

const isInReviewBucket = (status) => status === STATUS.SUBMITTED || status === STATUS.IN_REVIEW;

const isApprovedBucket = (status) => status === STATUS.APPROVED || status === 5;

export {
  STATUS,
  STATUS_LABEL,
  ACTIONS,
  ACTION_LABEL,
  isNoteRequiredForAction,
  canRecall,
  NOTE_MAX_LENGTH,
  NOTE_MIN_LENGTH,
  isOpenBucket,
  isInReviewBucket,
  isApprovedBucket,
};
